import { createFileRoute, Link } from "@tanstack/react-router";
import { serviceAreas } from "@/data/serviceAreas";
import { neighborhoodPages } from "@/data/neighborhoodPages";

const services: { to: string; label: string }[] = [
  { to: "/services/home-theater", label: "Home Theater" },
  { to: "/services/dolby-atmos-home-theater", label: "Dolby Atmos Home Theater" },
  { to: "/services/smart-home", label: "Smart Home" },
  { to: "/services/control4", label: "Control4 Home Automation" },
  { to: "/services/whole-home-audio", label: "Whole-Home Audio" },
  { to: "/services/hidden-audio", label: "Hidden Audio" },
  { to: "/services/whole-home-video", label: "Whole-Home Video" },
  { to: "/services/outdoor", label: "Outdoor AV" },
  { to: "/services/climate-control", label: "Climate Control" },
  { to: "/services/lighting", label: "Lighting Control" },
  { to: "/services/shading", label: "Motorized Shading" },
  { to: "/services/security", label: "Security" },
  { to: "/services/networking", label: "Networking" },
  { to: "/services/commercial-av", label: "Commercial AV Denver" },
  { to: "/services/conference", label: "Conference Rooms" },
  { to: "/services/prewire", label: "Prewire" },
  { to: "/services/retrofit", label: "Retrofit" },
  { to: "/services/tv-mounting", label: "TV Mounting" },
  { to: "/services/av-installation", label: "AV Installation Denver" },
  { to: "/services/voice-control", label: "Voice Control" },
];

const mainPages: { to: string; label: string }[] = [
  { to: "/", label: "Home" },
  { to: "/services", label: "All Services" },
  { to: "/service-areas", label: "Service Areas" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
  { to: "/blog", label: "Blog" },
  {
    to: "/blog/custom-home-theater-design-guide",
    label: "Custom Home Theater Design Guide",
  },
  { to: "/home-theater-installation-near-me", label: "Home Theater Installation Near Me" },
  { to: "/whole-home-audio-installation-near-me", label: "Whole-Home Audio Installation Near Me" },
  { to: "/home-theater-near-me", label: "Home Theater Near Me" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/cookies", label: "Cookie Policy" },
];

export const Route = createFileRoute("/sitemap")({
  head: () => ({
    meta: [
      { title: "Sitemap — Apex AV Denver" },
      { name: "description", content: "Full sitemap of Apex AV — browse every service, Denver-metro service area, and neighborhood landing page in one place." },
      { property: "og:title", content: "Sitemap — Apex AV Denver" },
      { property: "og:description", content: "Browse every Apex AV service, service area, and neighborhood page." },
      { property: "og:url", content: "https://apexav.com/sitemap" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Sitemap — Apex AV Denver" },
      { name: "twitter:description", content: "Browse every Apex AV service, service area, and neighborhood page." },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/sitemap" }],
  }),
  component: SitemapPage,
});

function SitemapPage() {
  return (
    <section className="container mx-auto px-6 py-20">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">Sitemap</p>
        <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">Everything on Apex AV.</h1>
        <p className="mt-6 text-lg text-muted-foreground">
          A complete index of our services, Denver-metro service areas, and neighborhood landing pages.
        </p>
      </div>

      <div className="mt-14 grid gap-12 lg:grid-cols-3">
        <div>
          <h2 className="font-display text-2xl font-bold border-b border-border pb-3">Main Pages</h2>
          <ul className="mt-5 space-y-3">
            {mainPages.map((p) => (
              <li key={p.to}>
                <Link to={p.to} className="text-foreground hover:text-brand-text transition">
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl font-bold border-b border-border pb-3">Services</h2>
          <ul className="mt-5 space-y-3">
            {services.map((s) => (
              <li key={s.to}>
                <Link to={s.to} className="text-foreground hover:text-brand-text transition">
                  {s.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="font-display text-2xl font-bold border-b border-border pb-3">Denver Suburbs & Service Areas</h2>
          <ul className="mt-5 space-y-3">
            {serviceAreas.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/service-areas/$slug"
                  params={{ slug: a.slug }}
                  className="text-foreground hover:text-brand-text transition"
                >
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-16">
        <h2 className="font-display text-2xl font-bold border-b border-border pb-3">Neighborhood Landing Pages</h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {neighborhoodPages.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              className="rounded-lg border border-border bg-card p-4 hover:border-brand/50 transition"
            >
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {p.neighborhoodName}
              </div>
              <div className="mt-1 font-medium">{p.label}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
